import { useQuery } from "@tanstack/react-query"
import { Plus } from "lucide-react"
import * as React from "react"
import { useNavigate } from "react-router"

import { Button } from "@/components/ui/button"
import { DataTable, Pagination, type RowKey, type SortState } from "@/components/ui/data-table"
import { EmptyState } from "@/components/ui/empty-state"
import { ActiveFilterChips } from "@/components/ui/table-filters"
import { type SortOption } from "@/components/ui/table-sort"
import { TableToolbar } from "@/components/ui/table-toolbar"
import { useToast } from "@/components/ui/toast"
import { NewReviewContainer } from "@/features/intake"
import { bannerFor } from "@/lib/api-error-message"
import { COMING_SOON, FEATURES } from "@/lib/features"
import { canOpenNewReview, reviewLandingPath } from "@/lib/roles"
import { useTableUrlState, type FilterValues, type TableSort } from "@/lib/table"
import { useSlashShortcut } from "@/lib/use-slash-shortcut"
import { cn } from "@/lib/utils"

import { threadsQuery, threadsSummaryQuery } from "./api"
import { useAssigneeFilterToast } from "./hooks/use-assignee-filter-toast"
import { useQueueFilters } from "./hooks/use-queue-filters"
import { useViewer } from "./hooks/use-viewer"
import { viewerIdAsMe } from "./queue-assignee-options"
import { QUEUE_TABLE_MIN_WIDTH, buildQueueColumns } from "./queue-columns"
import type { QueueRow } from "./queue-data"
import { ExpandedThread } from "./queue-expanded-thread"
import { buildThreadsParams } from "./queue-query-params"
import { QueueStatTiles } from "./queue-stat-tiles"

const SORT_OPTIONS: SortOption[] = [
  { id: "updatedAt", label: "Last updated", ascLabel: "Oldest first", descLabel: "Newest first" },
  { id: "product", label: "Product name", ascLabel: "A → Z", descLabel: "Z → A" },
  { id: "findings", label: "Findings count", ascLabel: "Fewest first", descLabel: "Most first" },
  { id: "versions", label: "Version count", ascLabel: "Fewest first", descLabel: "Most first" },
]

const SORT_FIELDS = SORT_OPTIONS.map((o) => o.id)
const DEFAULT_SORT: TableSort = { field: "updatedAt", dir: "desc" }
const EMPTY_ROWS: QueueRow[] = []
/** The legacy queue's page sizes. 100 is also the backend's cap. */
const QUEUE_PAGE_SIZES: readonly number[] = [10, 25, 50, 100]
const PAGE_SIZE_OPTIONS = [...QUEUE_PAGE_SIZES]

export function QueuePage(): JSX.Element {
  const { toast } = useToast()
  const navigate = useNavigate()
  const role = useViewer()?.role
  // No Assignee column for a client (queue-columns.tsx).
  const columns = React.useMemo(() => buildQueueColumns(role), [role])
  const searchRef = React.useRef<HTMLInputElement>(null)
  const [expanded, setExpanded] = React.useState<Set<RowKey>>(new Set())
  const [intakeOpen, setIntakeOpen] = React.useState(false)

  useSlashShortcut(searchRef)

  // Which filters the panel offers depends on who is signed in (Assignee).
  const {
    schema,
    assigneeScope,
    ready: filtersReady,
    rosterFailedAt,
    retryRoster,
  } = useQueueFilters()
  const mayOpenNewReview = canOpenNewReview(useViewer()?.role)

  // The URL is the query (lib/table/use-table-url-state.ts); React Query fetches whatever it says.
  // A link carrying the viewer's own id (a colleague picked them) means "Me", not "unknown".
  const normalizeFilters = React.useCallback(
    (values: FilterValues) => viewerIdAsMe(values, assigneeScope),
    [assigneeScope],
  )
  const { state, actions } = useTableUrlState({
    schema,
    sortFields: SORT_FIELDS,
    defaultSort: DEFAULT_SORT,
    pageSizes: QUEUE_PAGE_SIZES,
    normalizeFilters,
    clearSearchOnClearAll: true,
  })
  // A linked assignee dropped because the staff list failed is said, not swallowed.
  useAssigneeFilterToast({
    rosterFailedAt,
    droppedFilterIds: state.droppedFilterIds,
    onRetry: retryRoster,
  })
  // One set of params for the list and the tiles, so the tiles describe the list.
  const queueParams = {
    search: state.committedSearch,
    // Archived is a filter like any other (`?archived=1`), off unless asked for.
    filters: buildThreadsParams(state.filters, { assignees: assigneeScope }),
  }
  // Held until the Assignee options are known (useQueueFilters), so a linked
  // `?assignee=` is not fetched unfiltered first.
  const threads = useQuery({
    ...threadsQuery({
      ...queueParams,
      page: state.page,
      pageSize: state.pageSize,
      sort: state.sort,
    }),
    enabled: filtersReady,
  })
  const summary = useQuery({ ...threadsSummaryQuery(queueParams), enabled: filtersReady })
  const rows = threads.data?.items ?? EMPTY_ROWS
  const total = threads.data?.total ?? 0

  // A page number can outlive the rows behind it — a bookmarked `?page=6` after the
  // queue shrank to three pages. Walk back to the last page that exists, replacing
  // rather than pushing so Back does not return to the empty one (as legacy does).
  const servedTotal = threads.isSuccess ? threads.data.total : 0
  const pageIsEmpty = servedTotal > 0 && rows.length === 0
  const { setPage } = actions
  React.useEffect(() => {
    if (!pageIsEmpty) return
    const lastPage = Math.max(1, Math.ceil(servedTotal / state.pageSize))
    if (state.page > lastPage) setPage(lastPage, { replace: true })
  }, [pageIsEmpty, servedTotal, state.page, state.pageSize, setPage])

  const toggleExpand = React.useCallback((key: RowKey) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      next.has(key) ? next.delete(key) : next.add(key)
      return next
    })
  }, [])

  const setSort = React.useCallback(
    (s: SortState) => actions.setSort(s ? { field: s.columnId, dir: s.dir } : null),
    [actions],
  )

  const sortState: SortState = state.sort
    ? { columnId: state.sort.field, dir: state.sort.dir }
    : null

  // What the rows were fetched for — the URL — not the search box mid-debounce.
  const filtered = state.committedSearch.length > 0 || state.filterCount > 0

  return (
    <div className="flex h-full flex-col gap-5 px-6 py-5">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-subtitle text-text">Review queue</h1>
        {/* Absent, not disabled, for a viewer (as legacy): the API refuses them, so the
            button would be a door that can only be slammed. */}
        {mayOpenNewReview && (
          <Button
            variant="primary"
            leadingIcon={<Plus />}
            onClick={() => {
              if (FEATURES.newReview) setIntakeOpen(true)
              else toast(COMING_SOON)
            }}
          >
            New review
          </Button>
        )}
      </header>

      {FEATURES.newReview && mayOpenNewReview && (
        <NewReviewContainer
          open={intakeOpen}
          onOpenChange={setIntakeOpen}
          onCreated={(id) => navigate(reviewLandingPath(role, id))}
        />
      )}

      {threads.error &&
        (() => {
          const banner = bannerFor(threads.error, "the review queue")
          return (
            <div
              role="alert"
              className="rounded-md border border-people/30 bg-people-tint/40 px-4 py-3 text-body-sm text-people"
            >
              <div className="font-medium">{banner.title}</div>
              <div className="mt-0.5">{banner.body}</div>
            </div>
          )
        })()}

      <QueueStatTiles counts={summary.data} failed={summary.isError} />

      <TableToolbar
        search={state.search}
        onSearchChange={actions.setSearch}
        searchRef={searchRef}
        searchPlaceholder="Search product name or EPA Reg. No."
        searchAriaLabel="Search review queue"
        searchShortcut="/"
        filterSchema={schema}
        filterValues={state.filters}
        onFiltersChange={actions.setFilters}
        onClearAllFilters={actions.clearAllFilters}
        sortOptions={SORT_OPTIONS}
        sort={state.sort}
        onSortChange={actions.setSort}
      />

      <ActiveFilterChips
        schema={schema}
        values={state.filters}
        onRemove={actions.clearFilter}
        onClearAll={actions.clearAllFilters}
      />

      {/* As tall as its rows, as on Users: the pager sits under the last row rather than at
          the bottom of the window, and a long page scrolls inside the card. */}
      <div>
        <div className="flex flex-col overflow-hidden rounded-lg border border-border bg-surface">
          <div className="max-h-[calc(100dvh-320px)] overflow-auto">
            <DataTable<QueueRow>
              label="Review queue"
              columns={columns}
              rows={rows}
              rowKey={(r) => r.id}
              sort={sortState}
              onSortChange={setSort}
              expandedKeys={expanded}
              onToggleExpand={toggleExpand}
              renderExpanded={(row, layout) => <ExpandedThread row={row} layout={layout} />}
              stickyHeader
              loading={threads.isPending}
              // Held at its columns' minimum width, so on a narrow screen the wrapper
              // above scrolls sideways and no column is dropped.
              className={cn("rounded-none border-0", QUEUE_TABLE_MIN_WIDTH)}
              empty={
                <EmptyState
                  bare
                  reason={filtered ? "filtered" : "empty"}
                  title={filtered ? "Nothing matches" : "The queue is empty"}
                  body={
                    filtered
                      ? "Try a different term, or clear the search and filters to see the whole queue."
                      : "New submissions land here as they arrive."
                  }
                  action={
                    filtered ? (
                      <Button size="sm" onClick={actions.clearAllFilters}>
                        Clear filters
                      </Button>
                    ) : null
                  }
                />
              }
            />
          </div>
          <div className="border-t border-border">
            <Pagination
              page={state.page}
              pageSize={state.pageSize}
              total={total}
              onPageChange={setPage}
              onPageSizeChange={actions.setPageSize}
              pageSizes={PAGE_SIZE_OPTIONS}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
